export type WebQueueItState = "disabled" | "passed" | "unavailable";

const QUEUE_IT_TIMEOUT_MS = 15_000;
const allowedStates = new Set<WebQueueItState>([
  "disabled",
  "passed",
  "unavailable",
]);

type QueueItWindow = Window & {
  __whooshQueueItResolve?: (
    clubSlug: unknown,
    eventAlias: unknown,
    state: unknown
  ) => void;
};

const appendScript = ({
  attributes = {},
  onError,
  onLoad,
  src,
}: {
  attributes?: Record<string, string>;
  onError: () => void;
  onLoad?: () => void;
  src: string;
}) => {
  const script = document.createElement("script");

  script.async = false;
  script.onerror = onError;
  script.onload = onLoad ?? null;
  script.src = src;

  Object.entries(attributes).forEach(([name, value]) => {
    script.setAttribute(name, value);
  });

  document.head.appendChild(script);
};

/**
 * Loads Queue-it only after a club is explicitly enabled in LaunchDarkly.
 * Queue-it Integration Rules still select the waiting room from the current
 * browser URL; callbacks identify the club before settling this SPA gate after
 * verification. The event is deliberately not client configuration: it is
 * selected by Queue-it's published integration rule for the current URL.
 */
export const runWebQueueIt = (
  customerId: string,
  clubSlug: string
): Promise<WebQueueItState> =>
  new Promise((resolve) => {
    const queueItWindow = window as QueueItWindow;
    let isSettled = false;

    const settle = (state: WebQueueItState) => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      window.clearTimeout(timeout);
      resolve(state);
    };

    const timeout = window.setTimeout(
      () => settle("unavailable"),
      QUEUE_IT_TIMEOUT_MS
    );

    queueItWindow.__whooshQueueItResolve = (
      callbackClubSlug,
      _callbackEventAlias,
      state
    ) => {
      if (callbackClubSlug !== clubSlug) {
        return;
      }

      settle(
        allowedStates.has(state as WebQueueItState)
          ? (state as WebQueueItState)
          : "unavailable"
      );
    };

    appendScript({
      onError: () => settle("unavailable"),
      onLoad: () => {
        // Do not enable Queue-it's AJAX interception here. Its domain matcher
        // treats api.<host> as a match for <host>, which adds Queue-it headers
        // to our cross-origin GraphQL requests and causes CORS preflights to
        // fail. The integration rule itself still gates the current page.
        appendScript({
          attributes: {
            "data-queueit-c": customerId,
            // Patron is a React single-page application. This lets Queue-it
            // validate browser-history navigation after a visitor returns
            // from the waiting room instead of treating it as a new entry.
            "data-queueit-spa": "true",
          },
          onError: () => settle("unavailable"),
          src: "https://static.queue-it.net/script/queueconfigloader.min.js",
        });
      },
      src: "https://static.queue-it.net/script/queueclient.min.js",
    });
  });
